import { client } from '@/client';
import { Resource, SlideDeck, UpdateResourceRequest } from '@/client/courses';
import { Quiz } from '@/client/quizzes';
import { GenericForbiddenApiError } from '@/hooks/query';
import {
  COURSE_QUIZ_QUERY_KEY,
  COURSE_RESOURCE_QUERY_KEY,
  COURSE_SLIDE_DECK_QUERY_KEY,
} from '@/hooks/query/courses.query-keys';
import { useCallback } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

export const useCourseResource = (
  params: UseQueryOptions & {
    courseId?: string;
    resourceId?: string;
  },
): UseQueryResult<Resource> & {
  resource: Resource | undefined;
} => {
  const {
    courseId,
    resourceId,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [COURSE_RESOURCE_QUERY_KEY, courseId, resourceId],
    () =>
      courseId && resourceId
        ? client.courses.getCourseResource(courseId, resourceId)
        : undefined,
    {
      enabled: !!courseId && !!resourceId,
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as UseQueryResult<Resource>),
    },
  );

  return {
    resource: data,
    ...(rest as UseQueryResult<Resource>),
  };
};

export const usePrefetchCourseResource = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (courseId?: string, resourceId?: string) => {
      if (!courseId || !resourceId) return;

      queryClient.prefetchQuery(
        [COURSE_RESOURCE_QUERY_KEY, courseId, resourceId],
        () => client.courses.getCourseResource(courseId, resourceId),
        { staleTime: Infinity, cacheTime: Infinity },
      );
    },
    [queryClient],
  );
};

export type UpdateCourseResourcePayload = {
  courseId: string;
  resourceId: string;
  data: UpdateResourceRequest;
};

export const useUpdateCourseResource = () => {
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation<
    Resource,
    GenericForbiddenApiError,
    UpdateCourseResourcePayload
  >(
    'updateCourseResource',
    ({ courseId, resourceId, data }: UpdateCourseResourcePayload) =>
      client.courses.updateCourseResource(courseId, resourceId, data),
    {
      onSuccess: (_response, { courseId, resourceId }) => {
        queryClient.invalidateQueries([
          COURSE_RESOURCE_QUERY_KEY,
          courseId,
          resourceId,
        ]);
      },
    },
  );

  return { updateResource: mutate, isUpdatingResource: isLoading };
};

export const usePrefetchSlideDeck = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (courseId?: string, presentationId?: string) => {
      if (!courseId || !presentationId) return;

      queryClient.prefetchQuery(
        [COURSE_SLIDE_DECK_QUERY_KEY, courseId, presentationId],
        () => client.courses.getSlideDeck(courseId, presentationId),
        { staleTime: Infinity, cacheTime: Infinity },
      );
    },
    [queryClient],
  );
};

export const usePrefetchCourseQuiz = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (courseId?: string, quizId?: string) => {
      if (!courseId || !quizId) return;

      queryClient.prefetchQuery(
        [COURSE_QUIZ_QUERY_KEY, courseId, quizId],
        () => client.courses.getCourseQuiz(courseId, quizId),
        { staleTime: Infinity, cacheTime: Infinity },
      );
    },
    [queryClient],
  );
};

export const useSlideDeck = (
  params: UseQueryOptions & {
    courseId?: string;
    presentationId?: string;
  },
): UseQueryResult<SlideDeck> & {
  slideDeck: SlideDeck | undefined;
} => {
  const {
    courseId,
    presentationId,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [COURSE_SLIDE_DECK_QUERY_KEY, courseId, presentationId],
    () =>
      courseId && presentationId
        ? client.courses.getSlideDeck(courseId, presentationId)
        : undefined,
    {
      enabled: !!courseId && !!presentationId,
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as UseQueryResult<SlideDeck>),
    },
  );

  return {
    slideDeck: data,
    ...(rest as UseQueryResult<SlideDeck>),
  };
};

export const useCourseQuiz = (
  params: UseQueryOptions & {
    courseId?: string;
    quizId?: string;
  },
): UseQueryResult<Quiz> & {
  quiz: Quiz | undefined;
} => {
  const {
    courseId,
    quizId,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [COURSE_QUIZ_QUERY_KEY, courseId, quizId],
    () =>
      courseId && quizId
        ? client.courses.getCourseQuiz(courseId, quizId)
        : undefined,
    {
      enabled: !!courseId && !!quizId,
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as UseQueryResult<Quiz>),
    },
  );

  return {
    quiz: data,
    ...(rest as UseQueryResult<Quiz>),
  };
};
